/**
 * One collision rule for every Studio menu, select and popover that places
 * itself (StudioActionMenu and everything built on it: CursorSelect,
 * StudioMenu, StudioContextMenu). Radix menus and popovers get the same
 * behaviour from their own collision engine via the components/ui wrappers.
 *
 * - Anchored to a trigger: open below, start-aligned; if that runs past the
 *   right edge, align to the trigger end instead; flip above when there is
 *   no room below; finally shift so the card stays inside the viewport.
 * - Opened at a point (right-click / long-press): open right/below, flip
 *   left/above when it would cross an edge, then shift.
 */
export type FloatingRect = { left: number; right: number; top: number; bottom: number };

export const FLOATING_EDGE_PAD = 8;

export function placeFloating({
  width,
  height,
  anchor,
  point,
  placement = "below",
  align = "start",
  gap = 6,
  pad = FLOATING_EDGE_PAD,
}: {
  width: number;
  height: number;
  anchor?: FloatingRect | null;
  point?: { x: number; y: number } | null;
  /** `above` treats a point `y` as the menu bottom edge. */
  placement?: "above" | "below";
  /** Preferred horizontal alignment against the anchor. */
  align?: "start" | "end";
  gap?: number;
  pad?: number;
}): { left: number; top: number; maxWidth: number; maxHeight: number } {
  const viewport = typeof window !== "undefined" ? window.visualViewport : null;
  const vw = viewport?.width ?? (typeof window !== "undefined" ? window.innerWidth : 1024);
  const vh = viewport?.height ?? (typeof window !== "undefined" ? window.innerHeight : 768);
  const ox = viewport?.offsetLeft ?? 0;
  const oy = viewport?.offsetTop ?? 0;
  const maxWidth = Math.max(0, vw - pad * 2);
  const maxHeight = Math.max(0, vh - pad * 2);
  const w = Math.min(width, maxWidth);
  const h = Math.min(height, maxHeight);
  const minX = ox + pad;
  const maxX = ox + vw - pad;
  const minY = oy + pad;
  const maxY = oy + vh - pad;
  let left: number;
  let top: number;
  if (anchor) {
    const startLeft = anchor.left;
    const endLeft = anchor.right - w;
    if (align === "end") left = endLeft < minX && startLeft + w <= maxX ? startLeft : endLeft;
    else left = startLeft + w > maxX && endLeft >= minX ? endLeft : startLeft;
    const below = anchor.bottom + gap;
    const above = anchor.top - gap - h;
    const roomBelow = maxY - below;
    const roomAbove = anchor.top - gap - minY;
    if (placement === "above") top = above >= minY || roomAbove >= roomBelow ? above : below;
    else top = below + h <= maxY || roomBelow >= roomAbove ? below : above;
  } else if (point) {
    left = point.x + w > maxX && point.x - w >= minX ? point.x - w : point.x;
    if (placement === "above") {
      top = point.y - h - pad;
      if (top < minY) top = point.y + pad;
    } else {
      top = point.y + h > maxY && point.y - h >= minY ? point.y - h : point.y;
    }
  } else {
    left = ox + (vw - w) / 2;
    top = oy + (vh - h) / 2;
  }
  left = Math.max(minX, Math.min(left, maxX - w));
  top = Math.max(minY, Math.min(top, maxY - h));
  return { left, top, maxWidth, maxHeight };
}
